/**
 * Shared helpers for the StatefulMacroCoordinator.
 *
 * Kept out of the coordinator file so the coordinator stays focused on the
 * walk algorithm and stays within the project's file-size budget.
 */

import {
  MACRO_STEP_ERROR_CODES,
  macroStepErrorCodeFromLegacyProse,
  type MacroMessage,
  type MacroStepErrorCode,
  type MacroWalkResponsePayload,
} from '@playcastdotio/Messaging';

/**
 * Narrow a discriminated MacroMessage to its response payload portion.
 *
 * `MacroMessage` is `discriminatedUnion('type', [request, response])` — both
 * variants carry the base envelope plus a flat payload. Once we've matched
 * `type === MACRO_WALK_RESPONSE`, the message IS the payload (with extra
 * envelope fields). Cast through unknown for a focused, named view.
 */
export function extractWalkResponsePayload(message: MacroMessage): MacroWalkResponsePayload {
  return message as unknown as MacroWalkResponsePayload;
}

/**
 * Generate a fresh launch instance id used to scope the walk to a single
 * launch attempt. The host owns this id end-to-end — the API simply echoes
 * it back so we can drop late/duplicated responses.
 */
export function generateLaunchInstanceId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `walk-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

export type WaitCaptureErrorAction = 'retry' | 'restart' | 'legacy';

/** Typed macro-step failure carried on PlayjectorService rejections. */
export interface MacroStepError extends Error {
  readonly code: MacroStepErrorCode;
  /** True when the code was inferred from prose because the reply carried none. */
  readonly codeInferred: boolean;
  /** Raw native error prose when available — used only for version-skew inference. */
  readonly nativeError?: string;
}

export function isMacroStepError(error: unknown): error is MacroStepError {
  return (
    error instanceof Error
    && 'code' in error
    && typeof (error as MacroStepError).code === 'string'
    && MACRO_STEP_ERROR_CODES.includes((error as MacroStepError).code)
  );
}

export function createMacroStepError(
  message: string,
  code: MacroStepErrorCode,
  options: { codeInferred?: boolean; nativeError?: string } = {},
): MacroStepError {
  const err = new Error(message) as MacroStepError;
  Object.defineProperty(err, 'code', { value: code, enumerable: true });
  Object.defineProperty(err, 'codeInferred', { value: options.codeInferred ?? false, enumerable: true });
  if (options.nativeError !== undefined) {
    Object.defineProperty(err, 'nativeError', { value: options.nativeError, enumerable: true });
  }
  return err;
}

/** Exhaustive mapping — adding a MacroStepErrorCode without an action fails the build. */
function actionForMacroStepCode(code: MacroStepErrorCode): WaitCaptureErrorAction {
  switch (code) {
    case 'no_process':
      return 'restart';
    case 'process_not_ready':
    case 'no_hashes':
    case 'request_in_flight':
    case 'reply_timeout':
    case 'abandoned':
      return 'retry';
    case 'no_variants':
    case 'invalid_variant':
    case 'capture_failed':
      return 'legacy';
    default: {
      const exhaustive: never = code;
      return exhaustive;
    }
  }
}

const VERSION_SKEW_LOG_PREFIX = '[StatefulMacroSystemStep] Macro step error classified via legacy prose (version skew)';

/**
 * Classify Playjector macro-step failures while polling a Wait node.
 * Non-Wait captures intentionally retain the coordinator's legacy fallback.
 *
 * Exhaustive over MacroStepErrorCode — adding a code without an action fails the build.
 */
export function classifyWaitCaptureError(error: unknown): WaitCaptureErrorAction {
  if (isMacroStepError(error)) {
    if (error.codeInferred) {
      console.warn(`${VERSION_SKEW_LOG_PREFIX}: inferred ${error.code} from native prose`);
    }
    return actionForMacroStepCode(error.code);
  }

  if (error instanceof Error && typeof (error as MacroStepError).nativeError === 'string') {
    const nativeError = (error as MacroStepError).nativeError!;
    const inferred = macroStepErrorCodeFromLegacyProse(nativeError);
    if (inferred) {
      console.warn(`${VERSION_SKEW_LOG_PREFIX}: inferred ${inferred} from "${nativeError}"`);
      return actionForMacroStepCode(inferred);
    }
  }

  const message = error instanceof Error ? error.message : String(error);
  console.warn(
    '[StatefulMacroSystemStep] Unmapped macro step error classified as legacy — no errorCode and no legacy prose match',
    { message },
  );
  return 'legacy';
}
